import React, { ReactNode } from "react";
import { Card, CardContent, CardProps, styled, alpha } from "@mui/material";

// Create a styled Card component with custom styling
const StyledCardRoot = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  boxShadow:
    theme.palette.mode === "dark"
      ? `0 8px 32px ${alpha(theme.palette.common.black, 0.2)}`
      : `0 8px 32px ${alpha(theme.palette.common.black, 0.05)}`,
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.6)
      : alpha(theme.palette.background.paper, 0.8),
  backdropFilter: "blur(12px)",
  border: `1px solid ${theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.1)
      : alpha(theme.palette.common.black, 0.05)
    }`,
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: 20,
    padding: "2px",
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.5
    )} 0%, ${alpha(theme.palette.secondary.main, 0.5)} 100%)`,
    WebkitMaskImage: `linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)`,
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    pointerEvents: "none",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },

  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? `0 20px 40px ${alpha(theme.palette.common.black, 0.4)}`
        : `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
    "&::before": {
      opacity: 1,
    },
  },
}));

// Create a styled CardContent component
const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",

  "&:last-child": {
    paddingBottom: theme.spacing(3),
  },
}));

interface StyledCardProps extends CardProps {
  children: ReactNode;
}

const StyledCard: React.FC<StyledCardProps> = ({ children, ...props }) => {
  return (
    <StyledCardRoot {...props}>
      <StyledCardContent>{children}</StyledCardContent>
    </StyledCardRoot>
  );
};

export default StyledCard;
